import React, { ChangeEvent, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { Upload } from 'tus-js-client';
import { NOODLE_DOCUMENT_UPLOAD_HOST } from '@configuration/client';
import { nanoid } from 'nanoid';
import { logError } from '@providers/ErrorTracking';
import Bluebird from 'bluebird';
import { useUser } from '@hooks';

type Props = {
  documentRequestUserFileId: string;
  setIsUploading: (isUploading: boolean) => void;
  setUploadError: (e: Error | null) => void;
  onFinishUpload?: () => Promise<void>;
};

export type DocumentUploaderRef = {
  uploadDocument: () => void;
};

// eslint-disable-next-line prefer-arrow-callback
const DocumentUploader = forwardRef(function DocumentUploader({
  documentRequestUserFileId,
  setIsUploading,
  setUploadError,
  onFinishUpload,
}: Props, ref) {
  const inputFileRef = useRef<HTMLInputElement>(null);
  const [user] = useUser();

  useImperativeHandle(ref, () => ({
    uploadDocument: () => {
      inputFileRef?.current?.click();
    },
  }), []);

  const handleAddFile = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const fileToUpload = event.target.files?.item(0);
      if (!fileToUpload) {
        return;
      }
      if (!documentRequestUserFileId) {
        return;
      }
      if (!user) {
        return;
      }
      const headers: ConstructorParameters<typeof Upload>[1]['headers'] = {};
      if (user?.token) {
        headers.authorization = user.token;
      }
      let allUploadedTime = 0;
      setIsUploading(true);
      setUploadError(null);
      const upload = new Upload(fileToUpload, {
        endpoint: `${NOODLE_DOCUMENT_UPLOAD_HOST}/uploads`,
        headers,
        metadata: {
          contentType: fileToUpload.type,
          documentRequestUserFileId,
          filename: fileToUpload.name,
          filetype: fileToUpload.type,
          personId: user.id,
          // just to give context when looking at log insights
          referenceId: nanoid(),
        },
        onError: error => {
          // eslint-disable-next-line no-console
          console.log(`Failed because: ${error}`);
          logError(error);
          setUploadError(error);
          setIsUploading(false);
        },
        onProgress: (bytesUploaded, bytesTotal) => {
          const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
          // eslint-disable-next-line no-console
          console.log(bytesUploaded, bytesTotal, `${percentage}%`, Date.now());
          if (percentage === '100.00' && allUploadedTime === 0) {
            allUploadedTime = Date.now();
          }
        },
        onSuccess: async () => {
          // eslint-disable-next-line no-console
          console.log('Time from 100%', Date.now(), (Date.now() - allUploadedTime) / 1000);
          // eslint-disable-next-line no-console
          console.log('Download %s from %s', (upload.file as File)?.name || 'unknown', upload.url);

          // Delay to ensure document exists in document service
          await Bluebird.delay(1000);

          if (onFinishUpload) {
            await onFinishUpload();
          }

          setIsUploading(false);
        },
        retryDelays: [0, 3000, 5000, 10000, 20000],
      });
      upload.start();
    },
    [onFinishUpload, user, documentRequestUserFileId],
  );

  return (
    <input
      accept='application/pdf,image/*'
      id={documentRequestUserFileId}
      type="file"
      ref={inputFileRef}
      style={{
        display: 'none',
        height: 0,
        position: 'absolute',
        width: 0,
        zIndex: '-1',
      }}
      onChange={handleAddFile}
    />
  );
});

export default DocumentUploader;
